/**
 * Generic model localization from bounding box — docs/SIMULATION_LAB.md
 * v0: axis-aligned fit presets
 * v1: front axis + entry from exterior door leaves (feature map)
 */

import * as THREE from 'three';

const AXES = ['+z', '-z', '+x', '-x'];

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

/** Unit outward on XZ for a cardinal front axis. */
export function outwardOf(frontAxis) {
  switch (frontAxis) {
    case '-z':
      return [0, 0, -1];
    case '+x':
      return [1, 0, 0];
    case '-x':
      return [-1, 0, 0];
    case '+z':
    default:
      return [0, 0, 1];
  }
}

/**
 * Distance from a point to each bbox face (XZ).
 * @param {THREE.Box3} box3
 * @param {number} x
 * @param {number} z
 */
function faceDistances(box3, x, z) {
  return {
    '+z': Math.abs(box3.max.z - z),
    '-z': Math.abs(z - box3.min.z),
    '+x': Math.abs(box3.max.x - x),
    '-x': Math.abs(x - box3.min.x),
  };
}

/**
 * Guess front facade from exterior door leaf centers.
 * Blueprint: docs/SIMULATION_LAB.md · localize-v1
 *
 * @param {THREE.Box3} box3
 * @param {Array<{ center?: number[], confidence?: number, parentId?: string|null, kind?: string }>} doors
 * @returns {{
 *   frontAxis: '+z'|'-z'|'+x'|'-x',
 *   confidence: number,
 *   entryHint: { x: number, z: number } | null,
 *   source: 'doors' | 'default'
 * }}
 */
export function guessFrontFromDoors(box3, doors = []) {
  const leaves = (doors || []).filter(
    (d) =>
      d &&
      (!d.kind || d.kind === 'door') &&
      !d.parentId &&
      Array.isArray(d.center) &&
      d.center.length >= 3
  );

  if (!leaves.length || !box3) {
    return {
      frontAxis: '+z',
      confidence: 0.35,
      entryHint: null,
      source: 'default',
    };
  }

  const sizeX = box3.max.x - box3.min.x;
  const sizeZ = box3.max.z - box3.min.z;
  // Doors deeper than this from any face are treated as interior.
  const margin = Math.max(Math.min(sizeX, sizeZ) * 0.28, 1.1);

  let best = null;
  let bestLoose = null;

  for (const d of leaves) {
    const [x, , z] = d.center;
    const dist = faceDistances(box3, x, z);
    let face = '+z';
    let faceDist = dist['+z'];
    for (let i = 1; i < AXES.length; i += 1) {
      const ax = AXES[i];
      if (dist[ax] < faceDist) {
        face = ax;
        faceDist = dist[ax];
      }
    }

    const conf = Number.isFinite(d.confidence) ? d.confidence : 0.7;
    const faceScore = 1 - clamp(faceDist / margin, 0, 1);
    const score = conf * (0.35 + 0.65 * faceScore);
    const candidate = {
      frontAxis: face,
      score,
      confidence: clamp(0.5 + score * 0.45, 0.5, 0.95),
      entryHint: { x, z },
      faceDist,
    };

    if (!bestLoose || score > bestLoose.score) bestLoose = candidate;
    if (faceDist <= margin && (!best || score > best.score)) best = candidate;
  }

  const win = best || bestLoose;
  if (!win) {
    return {
      frontAxis: '+z',
      confidence: 0.35,
      entryHint: null,
      source: 'default',
    };
  }

  return {
    frontAxis: win.frontAxis,
    confidence: win.confidence,
    entryHint: win.entryHint,
    source: 'doors',
  };
}

/**
 * Point on the bbox front face at door height, plus an eye outside that face.
 * Lateral position follows entryHint (door) when provided.
 *
 * @param {THREE.Vector3} center
 * @param {THREE.Vector3} size
 * @param {number} floorY
 * @param {'+z'|'-z'|'+x'|'-x'} frontAxis
 * @param {{ x: number, z: number } | null} [entryHint]
 */
function entryFromBox(center, size, floorY, frontAxis, entryHint = null) {
  const outward = outwardOf(frontAxis);
  const halfX = size.x * 0.5;
  const halfZ = size.z * 0.5;
  const doorY = floorY + clamp(size.y * 0.42, 1.35, 1.85);

  // Keep lateral point on the face, inset slightly from corners.
  const insetX = halfX * 0.08;
  const insetZ = halfZ * 0.08;
  let faceX = clamp(
    entryHint?.x ?? center.x,
    center.x - halfX + insetX,
    center.x + halfX - insetX
  );
  let faceZ = clamp(
    entryHint?.z ?? center.z,
    center.z - halfZ + insetZ,
    center.z + halfZ - insetZ
  );

  if (frontAxis === '+z') faceZ = center.z + halfZ;
  else if (frontAxis === '-z') faceZ = center.z - halfZ;
  else if (frontAxis === '+x') faceX = center.x + halfX;
  else if (frontAxis === '-x') faceX = center.x - halfX;

  const point = [faceX, doorY, faceZ];
  const standOff = clamp(Math.max(size.x, size.z) * 0.35, 2.4, 8);
  const eye = [
    faceX + outward[0] * standOff,
    floorY + 1.65,
    faceZ + outward[2] * standOff,
  ];
  const ground = [
    faceX + outward[0] * 0.4,
    floorY + 0.15,
    faceZ + outward[2] * 0.4,
  ];

  return { point, outward, eye, ground, standOff };
}

/**
 * @param {THREE.Box3} box3
 * @param {{
 *   padding?: number,
 *   frontAxis?: '+z'|'-z'|'+x'|'-x',
 *   doorHints?: Array<{ center?: number[], confidence?: number, parentId?: string|null, kind?: string }>
 * }} [opts]
 */
export function localizeFromBox3(box3, opts = {}) {
  const padding = opts.padding ?? 1.25;
  const center = box3.getCenter(new THREE.Vector3());
  const size = box3.getSize(new THREE.Vector3());
  const floorY = box3.min.y;
  const span = Math.max(size.x, size.z, 1) * padding;
  const eyeHeight = floorY + clamp(size.y * 0.55, 1.4, 3.2);
  const lookY = center.y + size.y * 0.2;
  const lookAt = [center.x, lookY, center.z];

  let frontAxis = opts.frontAxis;
  let frontConfidence = 0.35;
  let entryHint = null;
  let frontSource = 'default';
  let version = 'v0';

  if (!frontAxis && opts.doorHints?.length) {
    const guess = guessFrontFromDoors(box3, opts.doorHints);
    frontAxis = guess.frontAxis;
    frontConfidence = guess.confidence;
    entryHint = guess.entryHint;
    frontSource = guess.source;
    version = guess.source === 'doors' ? 'v1' : 'v0';
  }

  if (!frontAxis) {
    frontAxis = '+z';
    frontConfidence = 0.35;
    frontSource = 'default';
    version = 'v0';
  } else if (opts.frontAxis) {
    frontConfidence = 1;
    frontSource = 'override';
    version = 'v1';
  }

  const axisEye = (axis, dist, y = eyeHeight) => {
    switch (axis) {
      case '-z':
        return [center.x, y, center.z - dist];
      case '+x':
        return [center.x + dist, y, center.z];
      case '-x':
        return [center.x - dist, y, center.z];
      case '+z':
      default:
        return [center.x, y, center.z + dist];
    }
  };

  const sideAxis =
    frontAxis === '+z' || frontAxis === '-z' ? '+x' : '+z';

  const entry = entryFromBox(center, size, floorY, frontAxis, entryHint);
  const approachDist = clamp(span * 1.6, entry.standOff + 4, span * 2.4);

  const presets = {
    iso: {
      eye: [
        center.x + span * 0.9,
        Math.max(center.y + span * 0.55, floorY + 3),
        center.z + span * 1.1,
      ],
      lookAt,
    },
    front: { eye: axisEye(frontAxis, span * 1.35), lookAt },
    side: { eye: axisEye(sideAxis, span * 1.35), lookAt },
    top: {
      eye: [center.x, center.y + span * 2.2, center.z + 0.05],
      lookAt: [center.x, floorY, center.z],
    },
    approach: {
      eye: [
        entry.point[0] + entry.outward[0] * approachDist,
        floorY + 1.7,
        entry.point[2] + entry.outward[2] * approachDist,
      ],
      lookAt: entry.ground,
    },
    entry: {
      eye: entry.eye,
      lookAt: entry.point,
    },
    corner: {
      eye: [
        center.x + span * 0.95,
        eyeHeight + 0.4,
        center.z + span * 0.95,
      ],
      lookAt,
    },
  };

  return {
    version,
    frontAxis,
    frontSource,
    floorY,
    center: center.toArray(),
    size: size.toArray(),
    span,
    entry,
    footprint: {
      cx: center.x,
      cz: center.z,
      width: size.x,
      depth: size.z,
      radius: Math.max(size.x, size.z) * 0.5,
    },
    presets,
    confidence: {
      front: frontConfidence,
      interior: 0.15,
      entry: frontSource === 'doors' ? frontConfidence : 0.4,
    },
  };
}

/** Map SoftPlan / Construx mesh names → roof | walls | interior */
export function layerKeyFromName(name = '') {
  const n = String(name).toLowerCase();
  if (
    n.startsWith('layer_roof') ||
    n === 'roof' ||
    n.startsWith('roof_')
  ) {
    return 'roof';
  }
  if (
    n.startsWith('layer_interior') ||
    n === 'ceiling' ||
    n.startsWith('ceiling_')
  ) {
    return 'interior';
  }
  if (
    n.startsWith('layer_walls') ||
    n.startsWith('floor_') ||
    n === 'walls'
  ) {
    return 'walls';
  }
  return 'walls';
}
